import {Arrays, byte, clamp, int, unitValue} from "@opendaw/lib-std"
import {MidiKeys} from "@opendaw/lib-dsp"
import {CubedStep} from "@opendaw/studio-adapters"
import {Rng} from "./Rng"

export type BassOptions = {
    seed: int
    root: byte
    scale: MidiKeys.PredefinedScale
    patterns: int
    density: unitValue
    slides: unitValue
    accents: unitValue
    octaveJumps: unitValue
    mutation: unitValue
}

export namespace BassGenerator {
    export const StepsPerPattern: int = 16

    export const defaults = (): BassOptions => ({
        seed: 1,
        root: 33,
        scale: MidiKeys.StockScales[10],
        patterns: 1,
        density: 0.7,
        slides: 0.25,
        accents: 0.3,
        octaveJumps: 0.15,
        mutation: 0.25
    })

    // One line, one key. The bank holds VARIATIONS of the same motif, never a transposition of it: an acid
    // line stays put and the movement comes from the filter, the accents and the slides.
    export const generate = (options: BassOptions): ReadonlyArray<ReadonlyArray<CubedStep>> => {
        const base = motif(options, new Rng(options.seed))
        return Arrays.create(index => {
            if (index === 0) {return base}
            const rng = new Rng(options.seed + index * 7919)
            return base.map(step => rng.next() < options.mutation
                ? {
                    ...step,
                    active: rng.next() < options.density,
                    accent: rng.next() < options.accents,
                    slide: step.slide && rng.next() < 0.5
                }
                : step)
        }, Math.max(1, options.patterns))
    }

    const motif = (options: BassOptions, rng: Rng): ReadonlyArray<CubedStep> => {
        const keys = Arrays.create(index => index, 12).filter(key => options.scale.has(key))
        const degrees = keys.length === 0 ? [0] : keys
        const steps: Array<CubedStep> = []
        let degree = 0
        for (let index = 0; index < StepsPerPattern; index++) {
            const onBeat = index % 4 === 0
            // The first step is the anchor: root, gated, so the ear always knows the key.
            const active = index === 0 || rng.next() < options.density
            const accent = index === 0 ? true : rng.next() < (onBeat ? options.accents * 2.0 : options.accents)
            degree = index === 0 ? 0 : clamp(degree + rng.nextInt(-2, 2), 0, degrees.length - 1)
            const octave = accent && rng.next() < options.octaveJumps ? 12 : 0
            steps.push({
                note: clamp(options.root + degrees[degree] + octave, 0, 127),
                active,
                accent,
                slide: false
            })
        }
        // A slide is a tie into the next note, so it only exists between two gated steps.
        for (let index = 0; index < steps.length; index++) {
            const next = steps[(index + 1) % steps.length]
            if (steps[index].active && next.active && rng.next() < options.slides) {
                steps[index] = {...steps[index], slide: true}
            }
        }
        return steps
    }
}
